import { Button } from "@/components/ui/button";
import { fmt, FinancialProfile } from "@/hooks/useHomeBuyingSim";
import { DollarSign, Wallet, Shield, TrendingUp, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Props {
  profile: FinancialProfile;
  onContinue: () => void;
  scenarioType: 'live' | 'default';
}

const cards = [
  { key: 'checking' as const, label: 'Checking', icon: Wallet, color: 'text-primary' },
  { key: 'savings' as const, label: 'Savings', icon: DollarSign, color: 'text-accent' },
  { key: 'emergencyFund' as const, label: 'Emergency Fund', icon: Shield, color: 'text-secondary' },
  { key: 'monthlyIncome' as const, label: 'Monthly Income', icon: TrendingUp, color: 'text-primary' },
  { key: 'monthlyExpenses' as const, label: 'Monthly Expenses', icon: CreditCard, color: 'text-destructive' },
];

export default function ScenarioSetup({ profile, onContinue, scenarioType }: Props) {
  const navigate = useNavigate();
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Your Home Buying Starting Point</h2>
        <p className="text-sm text-muted-foreground">
          {scenarioType === 'live'
            ? "Using your actual HeirWise balances."
            : "Using a sample financial profile."}
          {" "}Before buying a house, review the money you currently have available and how much room exists in your monthly budget.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={c.key} className={`glass-card p-4 rounded-xl space-y-1 ${i === cards.length - 1 && cards.length % 2 !== 0 ? 'col-span-2 sm:col-span-1' : ''}`}>
              <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${c.color}`} />
                <span className="text-xs text-muted-foreground">{c.label}</span>
              </div>
              <p className="font-heading font-bold text-foreground">{fmt(profile[c.key])}</p>
            </div>
          );
        })}
      </div>
      <div className="flex flex-col gap-3">
        <Button onClick={onContinue} className="w-full">Continue to House Shopping</Button>
        <Button variant="outline" onClick={() => navigate("/finances")} className="w-full">Review Accounts</Button>
      </div>
    </div>
  );
}
